import React, { useState, useEffect } from "react";
import { RefreshWarningModal } from "./modal/RefreshWarningModal";

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    isModelLoading: boolean;
    isTranscribing: boolean;
}

export function TranscribeButton(props: Props): JSX.Element {
    const { isModelLoading, isTranscribing, onClick, ...buttonProps } = props;
    const [showRefreshWarning, setShowRefreshWarning] = useState(false);
    const [refreshConfirmed, setRefreshConfirmed] = useState(false);

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isTranscribing && !refreshConfirmed) {
                e.preventDefault();
                setShowRefreshWarning(true);
                e.returnValue = '';
                return '';
            }
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', handleBeforeUnload);
            return () => window.removeEventListener('beforeunload', handleBeforeUnload);
        }
    }, [isTranscribing, refreshConfirmed]);

    const handleRefreshConfirm = () => {
        setRefreshConfirmed(true);
        window.location.reload();
    };

    return (
        <>
            <RefreshWarningModal
                isOpen={showRefreshWarning}
                onClose={() => setShowRefreshWarning(false)}
                onConfirm={handleRefreshConfirm}
                type="transcribing"
            />
            <button
                {...buttonProps}
                onClick={(event) => {
                    if (onClick && !isTranscribing && !isModelLoading) {
                        onClick(event);
                    }
                }}
                disabled={isTranscribing}
                className="text-white bg-primary hover:bg-primary-dark active:bg-primary-dark/90 font-medium rounded-xl text-base px-6 py-4 text-center inline-flex items-center justify-center min-w-[200px] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed select-none tap-highlight-none touch-manipulation active:animate-touch-press shadow-md hover:shadow-lg min-h-[var(--mobile-touch-target-size)] relative overflow-hidden group"
                aria-label={isModelLoading ? "Loading model" : isTranscribing ? "Transcribing audio" : "Transcribe audio"}
            >
                {/* Touch ripple effect element */}
                <span className="absolute inset-0 pointer-events-none touch-none opacity-0 group-active:opacity-100 group-active:animate-touch-ripple bg-white/20 rounded-full scale-0 origin-center"></span>
                
                {isModelLoading ? (
                    <Spinner text={"Loading model..."} />
                ) : isTranscribing ? (
                    <Spinner text={"Transcribing..."} />
                ) : (
                    <>
                        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                        </svg>
                        Transcribe Audio
                    </>
                )}
            </button>
        </>
    );
}

export function Spinner(props: { text: string }): JSX.Element {
    return (
        <div role="status" className="flex items-center gap-3">
            <svg
                aria-hidden="true"
                role="status"
                className="w-5 h-5 text-white animate-spin"
                viewBox='0 0 100 101'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
            >
                <path
                    d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
                    fill="currentColor"
                    className="opacity-20"
                />
                <path
                    d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
                    fill="currentColor"
                    className="opacity-100"
                />
            </svg>
            <span className="text-base font-medium">{props.text}</span>
        </div>
    );
}
